import { autoLink } from "vue-highlights"
import { MESSAGE_TYPES } from "@/lib/pusher-utils"

const ONE_MINUTE = 60000

export const QUERY_PARAMS = {
  SHOW_THREAD: "show_thread"
}

export const isMessageType = message =>
  [
    MESSAGE_TYPES.MESSAGE,
    MESSAGE_TYPES.IMAGE,
    MESSAGE_TYPES.VIDEO,
    MESSAGE_TYPES.ATTACHMENT
  ].includes(message.message_type)

export const isReactionType = message =>
  [message.message_type, message.messageType].includes(MESSAGE_TYPES.REACTION)

export const isSameUser = (message1, message2) => {
  return (
    isMessageType(message1) &&
    isMessageType(message2) &&
    message1.user.id === message2.user.id
  )
}

export const isSelf = (message, user) => {
  return isMessageType(message) && message.user.id === user.id
}

export const isSameAsPrevious = (messages, index, message) =>
  index > 0 && isSameUser(message, messages[index - 1])

export const isLastFromUser = (messages, index, message) =>
  isMessageType(message) &&
  (!messages[index + 1] || !isSameUser(message, messages[index + 1]))

export const isLatestInThread = (messages, i, message) =>
  isMessageType(message) && !messages[i + 1]

export const isFirstFromUser = (messages, index, message) =>
  isMessageType(message) &&
  (!messages[index - 1] || !isSameUser(message, messages[index - 1]))

export const buildMessageId = message =>
  `${message.message_thread_id}-${message.message_ts}`

export const parseMessageId = messageId => messageId.split("-")

export const MESSAGE_ORDER = {
  LATEST_FIRST: "desc",
  OLDEST_FIRST: "asc"
}

export const generatedMessagesFromLinkText = ({
  text,
  createdAt = +new Date()
}) => {
  const messages = []
  const beforeAttachments = []
  const afterAttachments = []
  const htmlContent = new DOMParser().parseFromString(
    autoLink(
      process.env.NODE_ENV === "development"
        ? text.replace("localhost", "localhost.dev")
        : text
    ),
    "text/html"
  ).body

  // Remove \n \t,... between urls
  htmlContent.childNodes.forEach(node => {
    if (!node.textContent.trim()) htmlContent.removeChild(node)
  })

  // Filter attachments before text message
  while (htmlContent.firstChild && htmlContent.firstChild.nodeName === "A") {
    beforeAttachments.push(htmlContent.firstChild)
    htmlContent.removeChild(htmlContent.firstChild)
  }

  // Filter attachments after text message
  while (htmlContent.lastChild && htmlContent.lastChild.nodeName === "A") {
    afterAttachments.unshift(htmlContent.lastChild)
    htmlContent.removeChild(htmlContent.lastChild)
  }

  beforeAttachments.forEach(attachment => {
    messages.push(
      buildAttachmentMessageForSubmission({
        attachment: attachment,
        createdAt: createdAt + messages.length
      })
    )
  })

  if (htmlContent.textContent.trim()) {
    messages.push({
      messageBody: htmlContent.textContent?.trim(),
      messageType: MESSAGE_TYPES.MESSAGE,
      messageCreatedAt: createdAt + messages.length
    })
  }

  afterAttachments.forEach(attachment => {
    messages.push(
      buildAttachmentMessageForSubmission({
        attachment: attachment,
        createdAt: createdAt + messages.length
      })
    )
  })

  return messages
}

const buildAttachmentMessageForSubmission = ({ attachment, createdAt }) => {
  return {
    attachmentData: {
      fetching: true,
      url: attachment.href
    },
    messageType: MESSAGE_TYPES.ATTACHMENT,
    messageCreatedAt: createdAt
  }
}

export const buildMessage = ({
  messageThreadId,
  user,
  body,
  imageUrls,
  videoUrl,
  attachmentData,
  messageTs,
  messageType,
  replyThreadId,
  groupTs,
  reactionCount = 0
}) => ({
  user,
  message: body,
  image_urls: imageUrls,
  video_url: videoUrl,
  attachment_data: attachmentData,
  created_at: messageTs,
  message_ts: messageTs,
  message_type: messageType,
  message_thread_id: messageThreadId,
  reply_thread_id: replyThreadId,
  group_ts: groupTs,
  reaction_count: reactionCount
})

export const buildMessageFromPusherData = (messageThreadId, pusherData) =>
  buildMessage({
    messageThreadId,
    user: pusherData.user,
    body: pusherData.messageBody,
    imageUrls: pusherData.imageUrls,
    videoUrl: pusherData.videoUrl,
    attachmentData: pusherData.attachmentData,
    messageTs: pusherData.messageTs || pusherData.messageCreatedAt,
    messageType: pusherData.messageType,
    replyThreadId: pusherData.replyThreadId,
    groupTs: pusherData.groupTs,
    reactionCount: pusherData.reactionCount
  })

export const groupMessages = messages => {
  const sortedMessages = messages.sort((a, b) => a.message_ts - b.message_ts)

  const groupedMessagesByGroupTs = sortedMessages
    .filter(message => message.group_ts)
    .reduce((acc, message) => {
      ;(acc[message.group_ts] = acc[message.group_ts] || []).push(message)

      return acc
    }, {})

  const groupedMessagesByMessageTs = sortedMessages
    .filter(message => !message.group_ts)
    .reduce((acc, message, index) => {
      if (
        isSameAsPrevious(messages, index, message) &&
        (acc["last_ts"]
          ? acc["last_ts"] + ONE_MINUTE > parseInt(message.message_ts)
          : true)
      ) {
        acc[acc["last_ts"]].push(message)
      } else {
        acc[message.message_ts] = [message]
        acc["last_ts"] = parseInt(message.message_ts)
      }

      return acc
    }, {})

  delete groupedMessagesByMessageTs["last_ts"]

  return { ...groupedMessagesByGroupTs, ...groupedMessagesByMessageTs }
}
